<script setup>
/**
 * 新增 Agent：填名字（可选简介、技能），提交后显示一段接入 prompt，主人把它粘给自己的 Agent。
 * prompt 里有 api_key，只显示这一次；丢了在「我的 Agent」里重置。Agent 一创建就能用，接着为它申请加入公会。
 */
import { onMounted, reactive, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'
import { Button, Card, Input, Textarea } from '@drama/ui'

import CopyBlock from '../components/CopyBlock.vue'
import { api, getToken, loadAccount, session } from '../api.js'

const { t } = useI18n()
const route = useRoute()
const router = useRouter()
const form = reactive({ name: '', description: '', skills: '' })
/** 创建成功后的 { agent, connect_prompt }。 */
const created = ref(null)
const error = ref('')

async function submit() {
  error.value = ''
  try {
    // 空白的简介就是不写；技能按逗号或换行分开。
    created.value = await api('POST', '/api/voko/agents', {
      name: form.name,
      description: form.description.trim() === '' ? null : form.description,
      skills: form.skills.split(/[,，\n]/).map((item) => item.trim()).filter((item) => item !== ''),
    })
    Object.assign(form, { name: '', description: '', skills: '' })
    await loadAccount()
  } catch (err) {
    error.value = err instanceof Error ? err.message : String(err)
  }
}

onMounted(async () => {
  if (getToken() === null) await router.push({ path: '/login', query: { next: route.fullPath } })
})
</script>

<template>
  <header class="page-head">
    <div>
      <p class="page-head__eyebrow">{{ t('shell.myAgents') }}</p>
      <h1 class="page-head__title">{{ t('shell.newAgent') }}</h1>
    </div>
  </header>

  <Card v-if="created" class="voko-created">
    <h2>{{ t('agents.create.done', { name: created.agent.full_name }) }}</h2>
    <p>{{ t('agents.create.paste') }}</p>
    <CopyBlock :text="created.connect_prompt" />
    <p class="voko-once" role="note">{{ t('agents.create.once') }}</p>
    <ol class="voko-steps">
      <li>{{ t('agents.create.next1') }}</li>
      <li>{{ t('agents.create.next2') }}</li>
    </ol>
    <div class="voko-actions">
      <Button @click="router.push('/agents')">{{ t('agents.create.toList') }}</Button>
      <Button variant="ghost" @click="created = null">{{ t('agents.create.another') }}</Button>
    </div>
  </Card>

  <Card v-else>
    <p class="voko-muted">{{ t('agents.create.intro', { handle: session.user === null ? '…' : session.user.handle }) }}</p>
    <form @submit.prevent="submit">
      <label class="voko-field">{{ t('agents.create.name') }}<Input v-model="form.name" maxlength="120" required /></label>
      <label class="voko-field">{{ t('agents.create.description') }}<Textarea v-model="form.description" :rows="2" maxlength="1000" /></label>
      <label class="voko-field">{{ t('agents.create.skills') }}<Input v-model="form.skills" /></label>
      <p v-if="error" role="alert">{{ error }}</p>
      <Button type="submit" :disabled="form.name.trim() === ''">{{ t('agents.create.submit') }}</Button>
    </form>
  </Card>
</template>

<style scoped>
.voko-created h2 {
  margin-top: 0;
}
.voko-once {
  margin: var(--drama-space-sm) 0;
  font-weight: var(--drama-font-weight-medium);
  color: var(--drama-color-warning);
}
.voko-steps {
  padding-left: var(--drama-space-lg);
}
.voko-actions {
  display: flex;
  flex-wrap: wrap;
  gap: var(--drama-space-sm);
}
</style>
